import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Loader2, Save, Trash2, Plus, Printer, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { StatusPill } from "./OpsDashboard";
import { useContent } from "@/contexts/ContentContext";

const $ = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUSES = ["draft", "sent", "paid", "overdue", "cancelled"];

export default function OpsInvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { content } = useContent();
  const site = content?.site;
  const [invoice, setInvoice] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [printMode, setPrintMode] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invoices/${id}`);
        setInvoice(data);
        setDraft({
          customer: { ...(data.customer || {}) },
          items: [...(data.items || [])],
          tax_rate: data.tax_rate || 0,
          due_date: data.due_date ? data.due_date.substring(0, 10) : "",
          notes: data.notes || "",
          status: data.status || "draft",
        });
      } catch {
        toast.error("Factura no encontrada");
        navigate("/admin/ops/invoices");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading || !draft || !invoice) {
    return <div className="p-10 text-white/60 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>;
  }

  const setCust = (k, v) => setDraft((d) => ({ ...d, customer: { ...d.customer, [k]: v } }));
  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setItem = (idx, k, v) => setDraft((d) => {
    const items = [...d.items];
    items[idx] = { ...items[idx], [k]: k === "description" ? v : Number(v) || 0 };
    return { ...d, items };
  });
  const addItem = () => setDraft((d) => ({ ...d, items: [...d.items, { description: "", quantity: 1, unit_price: 0 }] }));
  const removeItem = (idx) => setDraft((d) => ({ ...d, items: d.items.filter((_, i) => i !== idx) }));

  const subtotal = draft.items.reduce((s, it) => s + (it.quantity || 0) * (it.unit_price || 0), 0);
  const taxAmount = subtotal * (Number(draft.tax_rate) || 0) / 100;
  const total = subtotal + taxAmount;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        customer: draft.customer,
        items: draft.items.map((it) => ({
          description: it.description || "",
          quantity: Number(it.quantity) || 0,
          unit_price: Number(it.unit_price) || 0,
        })),
        tax_rate: Number(draft.tax_rate) || 0,
        due_date: draft.due_date || null,
        notes: draft.notes,
        status: draft.status,
      };
      const { data } = await api.patch(`/invoices/${id}`, payload);
      setInvoice(data);
      toast.success("Factura guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const markPaid = async () => {
    try {
      const { data } = await api.patch(`/invoices/${id}`, { status: "paid" });
      setInvoice(data);
      setDraft((d) => ({ ...d, status: "paid" }));
      toast.success("Marcada como pagada");
    } catch {
      toast.error("No se pudo actualizar");
    }
  };

  const del = async () => {
    if (!window.confirm("Eliminar esta factura?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Eliminada");
      navigate("/admin/ops/invoices");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const onPrint = () => {
    setPrintMode(true);
    setTimeout(() => { window.print(); setTimeout(() => setPrintMode(false), 500); }, 100);
  };

  if (printMode) {
    return <PrintableInvoice invoice={{ ...invoice, ...draft, subtotal, tax_amount: taxAmount, total }} site={site} />;
  }

  return (
    <div className="p-6 sm:p-10 max-w-4xl">
      <Link to="/admin/ops/invoices" className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-[#D4AF37] mb-5">
        <ChevronLeft className="w-3.5 h-3.5" /> Facturas
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">Factura</p>
            <StatusPill value={invoice.status} />
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">{invoice.number}</h1>
          <p className="text-white/45 text-xs mt-1">Emitida {new Date(invoice.issued_at).toLocaleDateString()}{invoice.paid_at && ` · Pagada ${new Date(invoice.paid_at).toLocaleDateString()}`}</p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          {draft.status !== "paid" && (
            <button onClick={markPaid} data-testid="invoice-mark-paid" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-bold bg-emerald-500 text-white hover:bg-emerald-400 transition-all">
              <CheckCircle2 className="w-4 h-4" /> Marcar pagada
            </button>
          )}
          <button onClick={onPrint} data-testid="invoice-print" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-bold bg-white text-[#0A0A0A] hover:bg-[#FBF8F1] transition-all">
            <Printer className="w-4 h-4" /> Imprimir / PDF
          </button>
          <button onClick={save} disabled={saving} data-testid="invoice-save" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-bold bg-[#D4AF37] text-[#0A0A0A] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar
          </button>
        </div>
      </div>

      {/* Customer */}
      <Section title="Cliente">
        <Row>
          <Field label="Nombre"><input type="text" value={draft.customer.full_name || ""} onChange={(e) => setCust("full_name", e.target.value)} className="input-dark" /></Field>
          <Field label="Teléfono"><input type="tel" value={draft.customer.phone || ""} onChange={(e) => setCust("phone", e.target.value)} className="input-dark" /></Field>
        </Row>
        <Row>
          <Field label="Email"><input type="email" value={draft.customer.email || ""} onChange={(e) => setCust("email", e.target.value)} className="input-dark" /></Field>
          <Field label="Dirección"><input type="text" value={draft.customer.address || ""} onChange={(e) => setCust("address", e.target.value)} className="input-dark" /></Field>
        </Row>
      </Section>

      {/* Items */}
      <Section title="Conceptos">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/45">
                <th className="pb-2 pr-2">Descripción</th>
                <th className="pb-2 px-2 w-20">Cant.</th>
                <th className="pb-2 px-2 w-28">Precio</th>
                <th className="pb-2 pl-2 w-24 text-right">Total</th>
                <th className="pb-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {draft.items.map((it, idx) => (
                <tr key={idx} className="border-t border-white/5">
                  <td className="py-2 pr-2"><input type="text" value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} className="input-dark !py-2" data-testid={`invoice-item-desc-${idx}`} /></td>
                  <td className="py-2 px-2"><input type="number" min="0" step="1" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} className="input-dark !py-2" /></td>
                  <td className="py-2 px-2"><input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} className="input-dark !py-2" /></td>
                  <td className="py-2 pl-2 text-right font-medium">{$(((it.quantity || 0) * (it.unit_price || 0)))}</td>
                  <td className="py-2 pl-2 text-right">
                    <button onClick={() => removeItem(idx)} className="text-white/40 hover:text-red-400 p-1.5"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button onClick={addItem} className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 hover:border-[#D4AF37]/40 transition-all" data-testid="invoice-add-item">
          <Plus className="w-3.5 h-3.5" /> Añadir concepto
        </button>
      </Section>

      {/* Totals */}
      <Section title="Totales e impuestos">
        <Row>
          <Field label="Tasa de impuesto (%)">
            <input type="number" step="0.01" min="0" value={draft.tax_rate} onChange={(e) => setField("tax_rate", e.target.value)} className="input-dark" placeholder="Ej: 6.25" data-testid="invoice-tax" />
          </Field>
          <Field label="Estado">
            <select value={draft.status} onChange={(e) => setField("status", e.target.value)} className="input-dark cursor-pointer" data-testid="invoice-status">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </Row>
        <Field label="Fecha de vencimiento">
          <input type="date" value={draft.due_date} onChange={(e) => setField("due_date", e.target.value)} className="input-dark" />
        </Field>

        <div className="mt-6 ml-auto max-w-xs space-y-1.5">
          <Line label="Subtotal" value={$(subtotal)} />
          <Line label={`Impuesto (${Number(draft.tax_rate) || 0}%)`} value={$(taxAmount)} />
          <div className="h-px bg-white/10 my-2" />
          <Line label="Total" value={$(total)} big />
        </div>
      </Section>

      <Section title="Notas">
        <textarea rows={3} value={draft.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="Notas para el cliente (aparecen en la factura impresa)…" className="input-dark resize-none" />
      </Section>

      <div className="mt-8 pt-6 border-t border-white/5 flex">
        <button onClick={del} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs text-white/55 hover:text-red-400 hover:bg-red-500/10 transition-all">
          <Trash2 className="w-3.5 h-3.5" /> Eliminar factura
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 sm:p-6 mb-5">
      <h3 className="font-display text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-4">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Row({ children }) { return <div className="grid sm:grid-cols-2 gap-4">{children}</div>; }
function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-white/55 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
function Line({ label, value, big }) {
  return (
    <div className={`flex items-center justify-between ${big ? "font-display text-lg font-bold text-[#D4AF37]" : "text-sm text-white/75"}`}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}

/* --------------- Printable view --------------- */
function PrintableInvoice({ invoice, site }) {
  return (
    <div className="min-h-screen bg-white text-[#0A0A0A] p-10 sm:p-14 font-body">
      <style>{`
        @media print {
          body { background: white !important; }
          @page { size: letter; margin: 0.5in; }
        }
      `}</style>

      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-8 pb-8 border-b-2 border-[#D4AF37]">
          <div>
            <h1 className="font-display text-3xl font-bold text-[#0A0A0A]">{site?.name || "M&C Clean Fresh"}</h1>
            <div className="text-sm text-gray-600 mt-2 space-y-0.5">
              <div>{site?.phone}</div>
              <div>{site?.email}</div>
              <div>Greater Boston, MA</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#B8941F] font-bold">Invoice</div>
            <div className="font-display text-2xl font-bold mt-1">{invoice.number}</div>
            <div className="mt-3 inline-block px-3 py-1 rounded-full bg-[#D4AF37]/15 text-[#B8941F] text-[10px] uppercase tracking-wider font-bold border border-[#D4AF37]/40">
              {invoice.status}
            </div>
          </div>
        </div>

        {/* Customer + dates */}
        <div className="grid grid-cols-2 gap-8 my-8 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-2">Bill To</div>
            <div className="font-display font-bold text-lg">{invoice.customer?.full_name}</div>
            {invoice.customer?.address && <div className="text-gray-700 mt-1">{invoice.customer.address}</div>}
            {invoice.customer?.email && <div className="text-gray-700">{invoice.customer.email}</div>}
            {invoice.customer?.phone && <div className="text-gray-700">{invoice.customer.phone}</div>}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-2">Issued</div>
            <div className="text-gray-800">{new Date(invoice.issued_at).toLocaleDateString()}</div>
            {invoice.due_date && (
              <>
                <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-bold mt-4 mb-2">Due</div>
                <div className="text-gray-800">{new Date(invoice.due_date).toLocaleDateString()}</div>
              </>
            )}
          </div>
        </div>

        {/* Items */}
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-[#0A0A0A]">
              <th className="text-left py-3 text-[10px] uppercase tracking-widest text-gray-600">Description</th>
              <th className="text-right py-3 text-[10px] uppercase tracking-widest text-gray-600 w-20">Qty</th>
              <th className="text-right py-3 text-[10px] uppercase tracking-widest text-gray-600 w-28">Unit</th>
              <th className="text-right py-3 text-[10px] uppercase tracking-widest text-gray-600 w-28">Total</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.items || []).map((it, idx) => (
              <tr key={idx} className="border-b border-gray-200">
                <td className="py-3">{it.description}</td>
                <td className="py-3 text-right">{it.quantity}</td>
                <td className="py-3 text-right">{$(it.unit_price)}</td>
                <td className="py-3 text-right">{$(it.quantity * it.unit_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="mt-6 ml-auto max-w-xs space-y-2 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span>{$(invoice.subtotal)}</span></div>
          <div className="flex justify-between"><span>Tax ({invoice.tax_rate}%)</span><span>{$(invoice.tax_amount)}</span></div>
          <div className="border-t-2 border-[#0A0A0A] pt-2 mt-2 flex justify-between font-display font-bold text-xl">
            <span>Total</span><span className="text-[#B8941F]">{$(invoice.total)}</span>
          </div>
        </div>

        {invoice.notes && (
          <div className="mt-10 pt-6 border-t border-gray-200 text-sm text-gray-700">
            <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-2">Notes</div>
            <p className="whitespace-pre-wrap">{invoice.notes}</p>
          </div>
        )}

        <div className="mt-16 text-center text-xs text-gray-500">
          Thank you for your business with {site?.name || "M&C Clean Fresh"}.
        </div>
      </div>
    </div>
  );
}
